/**
 * Durga Puja sale page.
 *
 * Everything happens in place: changing a variant re-prices and re-images its
 * row, adding to cart posts to the Cart AJAX API, and applying the coupon is the
 * only navigation — and only because Shopify's /discount/ route is the one
 * mechanism that genuinely attaches a code to the cart. Showing a discounted
 * price without doing that would be a promise the checkout breaks.
 */
(() => {
  const root = document.querySelector('[data-puja]');
  if (!root) return;

  const code = (root.dataset.code || '').trim();
  const pct = Number(root.dataset.pct || 0);
  const prepaidPct = Number(root.dataset.prepaidPct || 0);
  const hasCoupon = root.dataset.coupon !== 'false';
  const wantsFireworks = root.dataset.fireworks !== 'false';
  const strict = root.dataset.strict === 'true';
  const allowed = (root.dataset.pincodes || '')
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);

  const moneyFormat = window.Shopify?.money_format || '₹{{amount}}';

  /** Shopify prices are integer paise/cents. Formatting here rather than
   *  trusting a locale default keeps the page consistent with the theme. */
  function money(cents) {
    const amount = (cents / 100).toFixed(2);
    const [whole, frac] = amount.split('.');
    // Indian grouping: 1,23,456.78 rather than 123,456.78.
    const lead = whole.slice(0, -3);
    const tail = whole.slice(-3);
    const grouped = lead ? `${lead.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${tail}` : tail;
    return moneyFormat.includes('{{amount}}')
      ? moneyFormat.replace('{{amount}}', `${grouped}.${frac}`)
      : `₹${grouped}.${frac}`;
  }

  // --------------------------------------------------------------- motion

  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  /** Animations stay off the CPU when nobody is looking. A phone left on this
   *  page in someone's pocket should not keep compositing petals. */
  document.addEventListener('visibilitychange', () => {
    root.classList.toggle('puja--paused', document.hidden);
  });

  /** Stagger the row entrance without writing 200 CSS rules. */
  root.querySelectorAll('[data-product]').forEach((row, i) => {
    row.style.setProperty('--i', String(Math.min(i, 12)));
  });

  const burst = root.querySelector('[data-burst]');

  const SHELL_COLOURS = ['#f29f05', '#ffd166', '#ff6b6b', '#ffe9a8', '#f6c453'];

  /** One shell. Appends its own sparks and removes only those, so overlapping
   *  shells do not wipe each other the way a shared innerHTML reset did. */
  function burstAt(cx, cy, opts = {}) {
    if (reduceMotion || !burst) return;
    const count = opts.count ?? 18;
    const spread = opts.spread ?? 120;
    const colour = opts.colour ?? SHELL_COLOURS[0];
    const mine = [];
    burst.classList.add('is-on');
    for (let i = 0; i < count; i += 1) {
      const dot = document.createElement('span');
      dot.className = 'puja__spark';
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.3;
      const dist = spread * 0.75 + Math.random() * spread;
      dot.style.left = `${cx}px`;
      dot.style.top = `${cy}px`;
      dot.style.setProperty('--bx', `${Math.cos(angle) * dist}px`);
      dot.style.setProperty('--by', `${Math.sin(angle) * dist + 40}px`);
      dot.style.setProperty('--spark', colour);
      dot.style.animationDelay = `${Math.random() * 120}ms`;
      burst.appendChild(dot);
      mine.push(dot);
    }
    window.setTimeout(() => {
      mine.forEach((d) => d.remove());
      if (!burst.childElementCount) burst.classList.remove('is-on');
    }, 1500);
  }

  /** Landing fireworks: a handful of shells over the hero, staggered, once. */
  function fireworks() {
    if (reduceMotion || !burst) return;
    const hero = root.querySelector('.puja__hero');
    const box = hero?.getBoundingClientRect();
    const top = box ? box.top : 0;
    const height = box ? box.height : window.innerHeight * 0.7;
    const shells = window.innerWidth < 760 ? 4 : 6;
    for (let i = 0; i < shells; i += 1) {
      window.setTimeout(() => {
        burstAt(
          window.innerWidth * (0.12 + Math.random() * 0.76),
          top + height * (0.1 + Math.random() * 0.5),
          {
            count: 14 + Math.floor(Math.random() * 10),
            spread: 90 + Math.random() * 90,
            colour: SHELL_COLOURS[i % SHELL_COLOURS.length],
          }
        );
      }, 250 + i * 420);
    }
  }

  function celebrate(origin) {
    if (reduceMotion || !burst) return;
    const rect = origin?.getBoundingClientRect();
    const cx = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
    const cy = rect ? rect.top + rect.height / 2 : window.innerHeight / 3;
    burstAt(cx, cy);
  }

  /** Count the festival price down from the normal price so the saving is
   *  something the shopper watches happen rather than something they have to
   *  compare two columns to notice. */
  function animatePrice(el, fromCents, toCents) {
    if (reduceMotion || fromCents === toCents) { el.textContent = money(toCents); return; }
    const start = performance.now();
    const dur = 650;
    el.classList.add('is-flash');
    function step(now) {
      const t = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = money(Math.round(fromCents + (toCents - fromCents) * eased));
      if (t < 1) requestAnimationFrame(step);
      else window.setTimeout(() => el.classList.remove('is-flash'), 400);
    }
    requestAnimationFrame(step);
  }

  // -------------------------------------------------------------- countdown

  /** The offer has a fixed end moment, so there is nothing to "start" — this
   *  counts down to it. Parsed with an explicit +05:30 so every viewer sees the
   *  same remaining time regardless of their device timezone. */
  const endsAt = root.dataset.endsAt ? new Date(root.dataset.endsAt) : null;
  const cdBox = root.querySelector('[data-countdown]');
  const endedBox = root.querySelector('[data-ended]');
  let expired = false;

  function tick() {
    if (!endsAt || Number.isNaN(endsAt.getTime())) return;
    const ms = endsAt.getTime() - Date.now();

    if (ms <= 0) {
      if (!expired) {
        expired = true;
        root.classList.add('puja--over');
        if (cdBox) cdBox.hidden = true;
        if (endedBox) endedBox.hidden = false;
        // Re-price so festival columns stop advertising a discount that the
        // checkout will now refuse.
        repriceAll();
      }
      return;
    }

    if (cdBox) cdBox.hidden = false;
    const s = Math.floor(ms / 1000);
    const set = (sel, v) => {
      const el = root.querySelector(sel);
      if (el) el.textContent = String(v).padStart(2, '0');
    };
    set('[data-cd-d]', Math.floor(s / 86400));
    set('[data-cd-h]', Math.floor((s % 86400) / 3600));
    set('[data-cd-m]', Math.floor((s % 3600) / 60));
    set('[data-cd-s]', s % 60);
  }

  // ---------------------------------------------------------------- pricing

  function repriceRow(row) {
    const select = row.querySelector('[data-variant-select]');
    if (!select) return;

    const opt = select.tagName === 'SELECT' ? select.options[select.selectedIndex] : select;
    const price = Number(opt.dataset.price || 0);
    const compare = Number(opt.dataset.compare || 0);

    const mrpEl = row.querySelector('[data-mrp]');
    const spEl = row.querySelector('[data-sp]');
    const finalEl = row.querySelector('[data-final]');
    const saveEl = row.querySelector('[data-save]');
    const imgEl = row.querySelector('[data-img]');
    const addBtn = row.querySelector('[data-add]');

    // Fall back to the selling price when there is no compare-at, so the MRP
    // column never shows a struck-through zero.
    const mrp = compare > price ? compare : price;
    if (mrpEl) {
      mrpEl.textContent = money(mrp);
      mrpEl.style.visibility = compare > price ? 'visible' : 'hidden';
    }
    if (spEl) spEl.textContent = money(price);

    // Once the clock runs out the discount is gone, whatever the cookie says.
    const applied = isCodeApplied() && !expired;
    const effectivePct = applied ? pct : 0;
    const discounted = Math.round(price * (1 - effectivePct / 100));

    if (finalEl) {
      const shouldAnimate = applied && finalEl.dataset.animated !== 'done';
      if (shouldAnimate) {
        finalEl.dataset.animated = 'done';
        animatePrice(finalEl, price, discounted);
      } else {
        finalEl.textContent = applied ? money(discounted) : money(price);
      }
    }
    if (saveEl) {
      if (!hasCoupon) {
        // The offer is already in the price, so the saving is MRP minus price.
        // Nothing to prompt the shopper to do.
        const off = mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;
        saveEl.textContent = off > 0 ? `You save ${money(mrp - price)} · ${off}% off` : '';
      } else {
        saveEl.textContent = expired
          ? 'Festival offer has ended'
          : applied
            ? `You save ${money(price - discounted)}${prepaidPct ? ` · ${prepaidPct}% more online` : ''}`
            : 'Apply the coupon to see festival pricing';
      }
    }

    if (imgEl && opt.dataset.image) imgEl.src = opt.dataset.image;
    if (addBtn) addBtn.disabled = opt.disabled || (strict && pinBlocked);
  }

  function repriceAll() {
    root.querySelectorAll('[data-product]').forEach(repriceRow);
  }

  // ---------------------------------------------------------------- coupon

  /** The discount lives on the cart, not in our page state, so read Shopify's
   *  own cookie rather than remembering that we applied it. A stale local flag
   *  would show festival prices on a cart that has no discount on it. */
  function isCodeApplied() {
    if (!code) return false;
    return document.cookie.split(';').some((c) => {
      const [k, v] = c.split('=');
      return k.trim() === 'discount_code' && decodeURIComponent(v || '').toUpperCase() === code;
    });
  }

  const codeInput = root.querySelector('[data-code-input]');
  const codeMsg = root.querySelector('[data-code-msg]');

  function applyCode(entered) {
    const value = (entered || '').trim().toUpperCase();
    if (!value) return;
    if (code && value !== code) {
      codeMsg.textContent = 'That code is not valid for this offer.';
      codeMsg.className = 'puja__hint puja__hint--bad';
      return;
    }
    // Shopify's own route: attaches the code to the cart, then returns here.
    const back = window.location.pathname + window.location.search;
    window.location.href = `/discount/${encodeURIComponent(value)}?redirect=${encodeURIComponent(back)}`;
  }

  root.querySelector('[data-code-apply]')?.addEventListener('click', () => applyCode(codeInput?.value));
  codeInput?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); applyCode(codeInput.value); }
  });

  if (isCodeApplied() && codeMsg) {
    codeMsg.textContent = `Coupon ${code} applied — festival prices shown below.`;
    codeMsg.className = 'puja__hint puja__hint--good';
    if (codeInput) { codeInput.value = code; codeInput.readOnly = true; }
    // Fires once per arrival, including the hop back from /discount/ and the
    // QR landing, which is exactly the moment worth marking.
    if (root.dataset.celebrate !== 'false') {
      window.setTimeout(() => celebrate(codeMsg), 350);
    }
  }

  // --------------------------------------------------------------- pincode

  let pinBlocked = false;
  const pinInput = root.querySelector('[data-pin-input]');
  const pinMsg = root.querySelector('[data-pin-msg]');
  const PIN_KEY = 'puja_pin';

  function checkPin(value) {
    const pin = (value || '').replace(/\D/g, '');
    if (pin.length !== 6) {
      pinMsg.textContent = 'Enter a 6-digit PIN code.';
      pinMsg.className = 'puja__hint puja__hint--bad';
      return;
    }
    const ok = allowed.length === 0 || allowed.includes(pin);
    pinBlocked = !ok;
    pinMsg.textContent = ok
      ? 'Great — we deliver here during the festival.'
      : 'This offer is for Bhubaneswar deliveries only. You can still shop at regular prices.';
    pinMsg.className = `puja__hint puja__hint--${ok ? 'good' : 'bad'}`;
    try { localStorage.setItem(PIN_KEY, pin); } catch { /* private mode */ }
    repriceAll();
  }

  root.querySelector('[data-pin-check]')?.addEventListener('click', () => checkPin(pinInput?.value));
  pinInput?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); checkPin(pinInput.value); }
  });
  try {
    const saved = localStorage.getItem(PIN_KEY);
    if (saved && pinInput) { pinInput.value = saved; checkPin(saved); }
  } catch { /* ignore */ }

  // ------------------------------------------------------------ add to cart

  const cartBar = root.querySelector('[data-cartbar]');
  const cartCount = root.querySelector('[data-cart-count]');

  async function addToCart(row, button) {
    const select = row.querySelector('[data-variant-select]');
    const id = select?.tagName === 'SELECT' ? select.value : select?.value;
    if (!id) return;

    const original = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Could not add this item.');
      }
      button.textContent = 'Added ✓';
      celebrate(button);
      await refreshCart();
    } catch (err) {
      button.textContent = 'Try again';
      if (pinMsg) {
        pinMsg.textContent = err.message;
        pinMsg.className = 'puja__hint puja__hint--bad';
      }
    } finally {
      window.setTimeout(() => {
        button.disabled = false;
        button.textContent = original;
      }, 1600);
    }
  }

  async function refreshCart() {
    try {
      const cart = await (await fetch('/cart.js')).json();
      if (!cart.item_count) { cartBar.hidden = true; return; }
      cartBar.hidden = false;
      cartCount.textContent =
        `${cart.item_count} item${cart.item_count === 1 ? '' : 's'} · ${money(cart.total_price)}`;
    } catch { /* leave the bar as it is */ }
  }

  root.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-add]');
    if (!btn) return;
    const row = btn.closest('[data-product]');
    if (row) addToCart(row, btn);
  });

  root.addEventListener('change', (e) => {
    if (e.target.matches('[data-variant-select]')) {
      const row = e.target.closest('[data-product]');
      if (row) repriceRow(row);
    }
  });

  tick();
  window.setInterval(tick, 1000);
  repriceAll();
  refreshCart();

  // Landing fireworks. Deferred past first paint so the shells never compete
  // with the hero image decoding, and skipped when the tab opens in the
  // background -- a burst nobody saw is wasted work and a wasted moment.
  if (wantsFireworks && !reduceMotion) {
    const launch = () => { if (!document.hidden) fireworks(); };
    if (document.readyState === 'complete') window.setTimeout(launch, 200);
    else window.addEventListener('load', () => window.setTimeout(launch, 200), { once: true });
  }
})();
